import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils/cn";

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-lg border font-semibold w-fit whitespace-nowrap shrink-0 [&>svg]:pointer-events-none transition-colors overflow-hidden",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground",
        outline:
          "[color:var(--badge-neutral-text)] bg-transparent border-border",
        success:
          "[color:var(--badge-success-text)] [background:var(--badge-success-bg)] [border-color:var(--badge-success-border)]",
        error:
          "[color:var(--badge-error-text)] [background:var(--badge-error-bg)] [border-color:var(--badge-error-border)]",
        info: "[color:var(--badge-info-text)] [background:var(--badge-info-bg)] [border-color:var(--badge-info-border)]",
        warning:
          "[color:var(--badge-warning-text)] [background:var(--badge-warning-bg)] [border-color:var(--badge-warning-border)]",
        neutral:
          "[color:var(--badge-neutral-text)] [background:var(--badge-neutral-bg)] [border-color:var(--badge-neutral-border)]",
        archive:
          "[color:var(--badge-archive-text)] [background:var(--badge-archive-bg)] [border-color:var(--badge-archive-border)]",
        loading:
          "bg-accent animate-pulse border-transparent text-transparent min-w-[60px]",
      },
      size: {
        small: "px-[3px] py-[1px] text-sm gap-0",
        medium: "px-2 py-0.5 text-sm gap-1 [&>svg]:size-3 h-7",
        large: "px-2 py-1 text-sm gap-1 [&>svg]:size-4",
      },
      layout: {
        inline: "",
        wrap: "min-h-9 max-w-full justify-start whitespace-normal font-normal sm:max-w-lg",
        truncate:
          "min-h-9 w-auto min-w-0 shrink max-w-full justify-start font-normal sm:max-w-lg",
      },
      shape: {
        default: "",
        pill: "rounded-full px-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "medium",
      layout: "inline",
      shape: "default",
    },
  },
);

const Badge = React.forwardRef<
  HTMLSpanElement,
  React.ComponentProps<"span"> &
    VariantProps<typeof badgeVariants> & { asChild?: boolean }
>(
  (
    { className, variant, size, layout, shape, asChild = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "span";

    return (
      <Comp
        ref={ref}
        data-slot="badge"
        className={cn(
          badgeVariants({ variant, size, layout, shape }),
          className,
        )}
        {...props}
      />
    );
  },
);
Badge.displayName = "Badge";

export { Badge };
